import API, { gatewayFetch } from 'helpers/api';
import { apiURL } from 'config/baseURL';
import { store } from 'store/configStore';
import { queryStringify } from 'utils/router';
import { DashboardTabs } from 'pages/dashboardRevamp/dashboardUtils';
import { toast } from 'helpers/toast';
import {
	ExecutiveChartPayloadI,
	IssueListI,
	ReportListI,
	ExecutiveDownloadI,
	ExecutiveIssueTrackerI,
	DownloadReportViewI,
	HealthScoreDataI,
} from 'reducers/dashboardRevamp/executive/executive.types';
import { downloadURL } from 'utils/download';
import moment from 'moment';
import { getIsAllSelected } from './header.service';
import { filterObjectWithEmptyValues } from 'pages/dashboardRevamp/utils/filterObjectWithEmptyValues';
import { PAGE_TYPE } from 'components/dashboardRevamp/IssueTrackerModal/issueTrackerModalUtils';
import { buildQueryParams } from '../reports/operationalKPI.service';
import { getInternalToken } from 'services/bulkOpsRevamp/bulkEdit.service';

// Helper function to check if a category matches the selected filters
const isCategorySelected = (category: any, selectedCategories: string[]): boolean => {
	return (
		(category?._id && selectedCategories?.includes(category._id)) ||
		(category?.value && selectedCategories?.includes(category.value))
	);
};

export const fetchExecutiveHealth = async (selectedMetric: string): Promise<HealthScoreDataI | undefined> => {
	try {
		const { type, filters } = store.getState().executiveDashboard;
		const overallCategories = store.getState().admin.manage.questionCategories_data;
		const { isAllSites, isAllDepartments, isAllUsers, isAllQuestionnaires } = getIsAllSelected();

		let categories: string[] = [];

		if (type === DashboardTabs.Issues) {
			categories = overallCategories
				.filter((cat) => isCategorySelected(cat, filters?.categories || []))
				?.map((cat) => cat?.label);
			categories = categories.length === overallCategories.length ? [] : categories;
		}

		const query = JSON.stringify({
			metric: 'health',
			groupBy: selectedMetric,
			startDate: moment(filters?.startDate)?.format('YYYY-MM-DD') || '',
			endDate: moment(filters?.endDate)?.format('YYYY-MM-DD') || '',
			siteIDs: (!isAllSites && filters?.sites) || [],
			departmentIDs: !isAllDepartments ? filters?.departments : [],
			userIDs: !isAllUsers ? filters?.users : [],
			questionnaireIDs: !isAllQuestionnaires ? filters?.questionnaires : [],
			categories: categories || [],
			roles: filters?.roles || [],
		});
		const apiRoute = `issue/detail`;
		const authToken = await API.getFirebaseToken();
		const options = {
			method: 'POST',
			headers: {
				Authorization: authToken,
				'Content-Type': 'application/json',
			},
			body: query,
		};

		const url = `${apiURL}/statistics/dashboard/${apiRoute}`;
		const response = await gatewayFetch(url, options);

		if (response.status !== 200) throw new Error(`Error ${response.status}`);
		const responseData = await response.json();
		return responseData?.data as HealthScoreDataI;
	} catch (error) {
		toast.error(error);
	}
};

export const fetchIssueList = async (payload: ExecutiveChartPayloadI): Promise<IssueListI | undefined> => {
	try {
		const { type, filters } = store.getState().executiveDashboard;
		const overallCategories = store.getState().admin.manage.questionCategories_data;
		const { isAllSites, isAllDepartments, isAllUsers, isAllQuestionnaires } = getIsAllSelected();

		let categories: string[] =
			overallCategories.filter((cat) => isCategorySelected(cat, filters?.categories || []))?.map((cat) => cat?.label) ||
			[];

		categories = categories.length === overallCategories.length ? [] : categories;

		const query = JSON.stringify({
			metric: 'issueList',
			viewBy: payload.viewBy === 'irr' ? 'irr' : 'openIssueCount',
			sortBy: payload.sortBy,
			groupBy: payload.groupBy,
			sortDirection: payload.sortDirection,
			startDate: moment(filters?.startDate)?.format('YYYY-MM-DD') || '',
			endDate: moment(filters?.endDate)?.format('YYYY-MM-DD') || '',
			siteIDs: (!isAllSites && filters?.sites) || [],
			departmentIDs: !isAllDepartments ? filters?.departments : [],
			userIDs: !isAllUsers ? filters?.users : [],
			questionnaireIDs: !isAllQuestionnaires ? filters?.questionnaires : [],
			categories: categories || [],
			limit: 10,
			roles: filters?.roles || [],
		});

		const apiRoute = `issue`;

		const authToken = await API.getFirebaseToken();
		const options = {
			method: 'POST',
			headers: {
				Authorization: authToken,
				'Content-Type': 'application/json',
			},
			body: query,
		};
		const url = `${apiURL}/statistics/dashboard/${apiRoute}`;
		const response = await gatewayFetch(url, options);

		if (response.status !== 200) throw new Error(`Error ${response.status}`);
		const responseData = await response.json();
		return responseData?.data as IssueListI;
	} catch (error) {
		toast.error(error);
	}
};

export const fetchReportList = async (payload: ExecutiveChartPayloadI): Promise<ReportListI[] | undefined> => {
	try {
		const { type, filters } = store.getState().executiveDashboard;
		const { isAllSites, isAllDepartments, isAllUsers, isAllQuestionnaires } = getIsAllSelected();
		const query = JSON.stringify({
			metric: 'reportList',
			viewBy: 'completeCount',
			groupBy: payload.groupBy,
			sortBy: payload.sortBy,
			sortDirection: payload.sortDirection,
			startDate: moment(filters?.startDate)?.format('YYYY-MM-DD') || '',
			endDate: moment(filters?.endDate)?.format('YYYY-MM-DD') || '',
			siteIDs: (!isAllSites && filters?.sites) || [],
			departmentIDs: !isAllDepartments ? filters?.departments : [],
			userIDs: !isAllUsers ? filters?.users : [],
			questionnaireIDs: !isAllQuestionnaires ? filters?.questionnaires : [],
			limit: 10,
			roles: filters?.roles || [],
		});
		const apiRoute = `report`;
		const authToken = await API.getFirebaseToken();
		const options = {
			method: 'POST',
			headers: {
				Authorization: authToken,
				'Content-Type': 'application/json',
			},
			body: query,
		};
		const url = `${apiURL}/statistics/dashboard/${apiRoute}`;
		const response = await gatewayFetch(url, options);

		if (response.status !== 200) throw new Error(`Error ${response.status}`);
		const responseData = await response.json();
		return responseData?.data as ReportListI[];
	} catch (error) {
		toast.error(error);
	}
};

export const fetchDownloadReportView = async (
	payload: ExecutiveChartPayloadI,
): Promise<DownloadReportViewI[] | undefined> => {
	try {
		const { filters } = store.getState().executiveDashboard;
		const { isAllSites, isAllDepartments, isAllUsers, isAllQuestionnaires } = getIsAllSelected();

		let fieldName: string[] = [
			'adhoc',
			'city',
			'totalSites',
			'totalDepartments',
			'totalQuestionnaires',
			'totalUsers',
			'totalSchedules',
			'avgReportCompletionTime',
			'minReportCompletionTime',
			'maxReportCompletionTime',
			'avgReportCompletionRate',
			'totalSchedules',
		];

		if (payload.groupBy === 'site') {
			fieldName.push('siteName');
		}

		const query = JSON.stringify({
			startDate: moment(filters?.startDate)?.format('YYYY-MM-DD') || '',
			endDate: moment(filters?.endDate)?.format('YYYY-MM-DD') || '',
			siteIDs: (!isAllSites && filters?.sites) || [],
			departmentIDs: !isAllDepartments ? filters?.departments : [],
			userIDs: !isAllUsers ? filters?.users : [],
			questionnaireIDs: !isAllQuestionnaires ? filters?.questionnaires : [],
			groupBy: payload.groupBy,
			sortDirection: payload.sortDirection,
			sortBy: 'completed',
			fieldName: fieldName,
			roles: filters?.roles || [],
		});

		const firebaseToken = await API.getFirebaseToken();
		const url = `${apiURL}/statistics/dashboard/report/download/view`;
		const internalToken = await getInternalToken(firebaseToken, url);
		const options = {
			method: 'POST',
			headers: {
				Authorization: internalToken,
				'Content-Type': 'application/json',
			},
			body: query,
		};
		const response = await gatewayFetch(url, options);

		if (response.status !== 200) throw new Error(`Error ${response.status}`);
		const responseData = await response.json();
		return responseData?.data as DownloadReportViewI[];
	} catch (error) {
		toast.error(error);
	}
};

export const fetchDownloadReportFile = async (fieldQuery: object, fileType: string) => {
	try {
		const { filters } = store.getState().executiveDashboard;
		const { isAllSites, isAllDepartments, isAllUsers, isAllQuestionnaires } = getIsAllSelected();

		const query = filterObjectWithEmptyValues({
			startDate: moment(filters?.startDate)?.format('YYYY-MM-DD') || '',
			endDate: moment(filters?.endDate)?.format('YYYY-MM-DD') || '',
			siteIDs: (!isAllSites && filters?.sites) || [],
			departmentIDs: !isAllDepartments ? filters?.departments : [],
			userIDs: !isAllUsers ? filters?.users : [],
			questionnaireIDs: !isAllQuestionnaires ? filters?.questionnaires : [],
			...fieldQuery,
			roles: filters?.roles || [],
		});

		const firebaseToken = await API.getFirebaseToken();
		const url = `${apiURL}/statistics/dashboard/report/download`;
		const internalToken = await getInternalToken(firebaseToken, url);
		const options = {
			method: 'POST',
			headers: {
				Authorization: internalToken,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(query),
		};
		const response = await gatewayFetch(url, options);

		if (response.status !== 200) throw new Error(`Error ${response.status}`);
		const responseData = await response.json();
		if (responseData?.data) {
			downloadURL(responseData?.data, `nimbly-dashboard-report-${moment().format('DD-MM-YY')}.${fileType}`);
		}
	} catch (error) {
		toast.error(error);
	}
};

export const fetchExecutiveDownload = async (payload: {
	groupBy: string;
	sortDirection: string;
	fieldName: string[];
}): Promise<ExecutiveDownloadI | undefined> => {
	try {
		const { filters } = store.getState().executiveDashboard;
		const overallCategories = store.getState().admin.manage.questionCategories_data;
		const { isAllSites, isAllDepartments, isAllUsers, isAllQuestionnaires } = getIsAllSelected();

		let categories: string[] = overallCategories
			.filter((cat) => isCategorySelected(cat, filters?.categories || []))
			?.map((cat) => cat?.label);

		categories = categories.length === overallCategories.length ? [] : categories;
		// check if fieldName is empty, if so, set it to default values
		if (payload.fieldName.length === 0) {
			payload.fieldName = ['siteName'];
		}

		const query = filterObjectWithEmptyValues({
			fieldName: payload.fieldName,
			groupBy: payload.groupBy,
			sortDirection: payload.sortDirection,
			startDate: moment(filters?.startDate)?.format('YYYY-MM-DD') || '',
			endDate: moment(filters?.endDate)?.format('YYYY-MM-DD') || '',
			limit: 10,
			sortBy: 'completed',
			siteIDs: (!isAllSites && filters?.sites) || [],
			departmentIDs: !isAllDepartments ? filters?.departments : [],
			userIDs: !isAllUsers ? filters?.users : [],
			questionnaireIDs: !isAllQuestionnaires ? filters?.questionnaires : [],
			categories: categories || [],
			roles: filters?.roles || [],
		});

		const apiRoute = `issue/download/view`;

		const authToken = await API.getFirebaseToken();
		const options = {
			method: 'POST',
			headers: {
				Authorization: authToken,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(query),
		};
		const url = `${apiURL}/statistics/dashboard/${apiRoute}`;
		const response = await gatewayFetch(url, options);

		if (response.status !== 200) throw new Error(`Error ${response.status}`);
		const responseData = await response.json();
		return responseData?.data as ExecutiveDownloadI;
	} catch (error) {
		toast.error(error);
	}
};

export const fetchExecutiveIssueTracker = async (
	payload: {
		id: string;
		onlyOpen: boolean;
		groupBy: string;
		sortBy: string;
		sortDirection: string;
		limit: number;
		page: number;
	},
	pageType: PAGE_TYPE,
): Promise<ExecutiveIssueTrackerI | undefined> => {
	try {
		let query: any;
		if (pageType === PAGE_TYPE.EXEC_PAGE) {
			const { filters } = store.getState().executiveDashboard;
			const overallCategories = store.getState().admin.manage.questionCategories_data;
			const { isAllSites, isAllDepartments, isAllUsers, isAllQuestionnaires } = getIsAllSelected();

			let categories: string[] =
				overallCategories
					.filter((cat) => isCategorySelected(cat, filters?.categories || []))
					?.map((cat) => cat?.label) || [];

			categories = categories.length === overallCategories.length ? [] : categories;

			query = filterObjectWithEmptyValues({
				id: payload.id,
				startDate: moment(filters?.startDate)?.format('YYYY-MM-DD') || '',
				endDate: moment(filters?.endDate)?.format('YYYY-MM-DD') || '',
				onlyOpen: `${payload.onlyOpen}`,
				groupBy: payload.groupBy,
				sortBy: payload.sortBy,
				sortDirection: payload.sortDirection,
				limit: payload.limit,
				page: payload.page,
				siteIDs: (!isAllSites && filters?.sites) || [],
				departmentIDs: !isAllDepartments ? filters?.departments : [],
				userIDs: !isAllUsers ? filters?.users : [],
				questionnaireIDs: !isAllQuestionnaires ? filters?.questionnaires : [],
				categories: categories || [],
				roles: filters?.roles || [],
			});
		} else if (pageType === PAGE_TYPE.OPS_KPI) {
			const queryParams = buildQueryParams();
			query = filterObjectWithEmptyValues({
				...queryParams,
				id: payload.id,
				onlyOpen: `${payload.onlyOpen}`,
				groupBy: payload.groupBy,
				sortBy: payload.sortBy,
				sortDirection: payload.sortDirection,
				limit: payload.limit,
				page: payload.page,
			});
		} else if (pageType === PAGE_TYPE.ISSUE_INSIGHTS) {
			const queryParams = buildQueryParams();
			query = filterObjectWithEmptyValues({
				...queryParams,
				id: payload.id,
				onlyOpen: `${payload.onlyOpen}`,
				groupBy: payload.groupBy,
				sortBy: payload.sortBy,
				sortDirection: payload.sortDirection,
				limit: payload.limit,
				page: payload.page,
			});
		}

		const apiRoute = `issue/list`;

		const authToken = await API.getFirebaseToken();
		const options = {
			method: 'POST',
			headers: {
				Authorization: authToken,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(query),
		};
		const url = `${apiURL}/statistics/dashboard/${apiRoute}`;
		const response = await gatewayFetch(url, options);

		if (response.status !== 200) throw new Error(`Error ${response.status}`);
		const responseData = await response.json();
		return responseData?.data as ExecutiveIssueTrackerI;
	} catch (error) {
		toast.error(error);
	}
};

export const fetchDownloadIssueFile = async (fieldQuery: object, fileType: string) => {
	try {
		const { filters } = store.getState().executiveDashboard;
		const { isAllSites, isAllDepartments, isAllUsers, isAllQuestionnaires } = getIsAllSelected();

		const query = filterObjectWithEmptyValues({
			startDate: moment(filters?.startDate)?.format('YYYY-MM-DD') || '',
			endDate: moment(filters?.endDate)?.format('YYYY-MM-DD') || '',
			siteIDs: (!isAllSites && filters?.sites) || [],
			departmentIDs: !isAllDepartments ? filters?.departments : [],
			userIDs: !isAllUsers ? filters?.users : [],
			questionnaireIDs: !isAllQuestionnaires ? filters?.questionnaires : [],
			roles: filters?.roles || [],
			...fieldQuery,
		});

		const authToken = await API.getFirebaseToken();
		const options = {
			method: 'POST',
			headers: {
				Authorization: authToken,
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(query),
		};
		const url = `${apiURL}/statistics/dashboard/issue/download`;
		const response = await gatewayFetch(url, options);

		if (response.status !== 200) throw new Error(`Error ${response.status}`);
		const responseData = await response.json();
		if (responseData?.data) {
			downloadURL(responseData?.data, `nimbly-dashboard-issue-${moment().format('DD-MM-YY')}.${fileType}`);
		}
	} catch (error) {
		toast.error(error);
	}
};
